import { NavLink } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  LayoutDashboard,
  Play,
  Flame,
  Filter,
  FormInput,
  AlertTriangle,
  MessageSquare,
  Sparkles,
  CreditCard,
  Settings,
  ChevronDown,
  Globe,
  ShoppingCart,
  Target,
} from 'lucide-react';
import clsx from 'clsx';
import { getSites, getUsage, type Site, type UsageData } from '../../api/client';
import { SITE_KEY } from '../../utils/constants';

const navItems = [
  { label: 'Dashboard', path: '/', icon: LayoutDashboard },
  { label: 'Sessions', path: '/sessions', icon: Play },
  { label: 'Landing Pages', path: '/landing-pages', icon: Target },
  { label: 'Heatmaps', path: '/heatmaps', icon: Flame },
  { label: 'Funnels', path: '/funnels', icon: Filter },
  { label: 'Checkout', path: '/checkout-insights', icon: ShoppingCart },
  { label: 'Forms', path: '/forms', icon: FormInput },
  { label: 'Errors', path: '/errors', icon: AlertTriangle },
  { label: 'Surveys', path: '/surveys', icon: MessageSquare },
  { label: 'AI Insights', path: '/insights', icon: Sparkles },
  { label: 'Billing', path: '/billing', icon: CreditCard },
  { label: 'Settings', path: '/settings', icon: Settings },
];

function usageBarColor(percent: number): string {
  if (percent >= 80) return 'bg-red-500';
  if (percent >= 60) return 'bg-yellow-500';
  return 'bg-green-500';
}

export default function Sidebar() {
  const [sites, setSites] = useState<Site[]>([]);
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [activeSiteId, setActiveSiteId] = useState<string>(
    () => localStorage.getItem(SITE_KEY) || '',
  );
  const [siteDropdownOpen, setSiteDropdownOpen] = useState(false);

  useEffect(() => {
    getSites()
      .then((siteList) => {
        setSites(siteList || []);
        if (!activeSiteId && siteList.length > 0) {
          setActiveSiteId(siteList[0].id);
          localStorage.setItem(SITE_KEY, siteList[0].id);
        }
      })
      .catch(() => {
        setSites([]);
      });
    getUsage()
      .then((data) => setUsage(data))
      .catch(() => {});
  }, [activeSiteId]);

  const activeSite = sites.find((s) => s.id === activeSiteId);

  function handleSiteChange(siteId: string) {
    setActiveSiteId(siteId);
    localStorage.setItem(SITE_KEY, siteId);
    setSiteDropdownOpen(false);
    window.location.reload();
  }

  return (
    <aside className="flex h-full w-60 flex-col bg-sidebar text-gray-300">
      {/* Logo */}
      <div className="flex h-16 items-center gap-2 px-5">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-600">
          <span className="text-sm font-bold text-white">V</span>
        </div>
        <span className="text-lg font-semibold text-white">Vantage</span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 px-3 py-4">
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-sidebar-active text-white'
                    : 'text-gray-400 hover:bg-sidebar-hover hover:text-white',
                )
              }
            >
              <Icon className="h-5 w-5 shrink-0" />
              {item.label}
            </NavLink>
          );
        })}
      </nav>

      {/* Usage Indicator */}
      {usage && (
        <div className="border-t border-gray-700 px-4 py-3">
          <div className="mb-1.5 flex items-center justify-between text-xs text-gray-400">
            <span>{(usage.sessionCount || 0).toLocaleString()} / {(usage.sessionLimit || 0).toLocaleString()} sessions</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-700">
            <div
              className={clsx('h-full rounded-full transition-all', usageBarColor(usage.percentUsed || 0))}
              style={{ width: `${Math.min(usage.percentUsed || 0, 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Site Selector */}
      <div className="border-t border-gray-700 p-3">
        <div className="relative">
          <button
            onClick={() => setSiteDropdownOpen(!siteDropdownOpen)}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-gray-400 transition-colors hover:bg-sidebar-hover hover:text-white"
          >
            <Globe className="h-4 w-4 shrink-0" />
            <span className="flex-1 truncate text-left">
              {activeSite?.name || 'Select site'}
            </span>
            <ChevronDown
              className={clsx('h-4 w-4 transition-transform', siteDropdownOpen && 'rotate-180')}
            />
          </button>
          {siteDropdownOpen && sites.length > 0 && (
            <div className="absolute bottom-full left-0 mb-1 w-full rounded-lg bg-sidebar-hover py-1 shadow-lg">
              {sites.map((site) => (
                <button
                  key={site.id}
                  onClick={() => handleSiteChange(site.id)}
                  className={clsx(
                    'block w-full px-3 py-2 text-left text-sm transition-colors',
                    site.id === activeSiteId
                      ? 'bg-sidebar-active text-white'
                      : 'text-gray-400 hover:text-white',
                  )}
                >
                  {site.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
